import {
  Check,
  CircleDot,
  Clock,
  UserCheck,
  UserX,
  Wand2,
  Flag,
  Tag,
  Bot,
  Mail,
  MailOpen,
  User,
  ShieldCheck,
  Ticket,
  Smartphone,
  StickyNote,
  Ban,
  Link2,
  ExternalLink,
  Trash2,
  Users,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import type { MenuNode } from './ContextMenu';
import { api, inboxApi } from '../lib/api';
import type { Conversation, Label, Team } from '../lib/types';

export interface ConvMenuCtx {
  chat: Conversation;
  selectedIds: string[];
  tenantCode: string;
  agentUid: string;
  role?: string;
  labels: Label[];
  teams?: Team[];
  /** WP-backed tenant (Bekesher): hide native Customer 360 / Manage lines. */
  wpConfigured?: boolean;
  /** UI density. "Move to waiting" stays advanced-only HERE, alongside the rest
   *  of the row/bulk tooling this menu belongs to (bulk select is advanced-only
   *  too). It is not unreachable in the default view: the parked queues have a
   *  tab there now, and the conversation header's ⋯ menu parks and un-parks. */
  advanced?: boolean;
  navigate: (to: string) => void;
  clearSelection: () => void;
}

const PRIORITIES: Array<{ value: 'urgent' | 'high' | 'medium' | 'low'; label: string }> = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

function fail(err: unknown) {
  alert(`Action failed: ${err instanceof Error ? err.message : String(err)}`);
}

export function buildConversationMenu(ctx: ConvMenuCtx): MenuNode[] {
  const { chat, tenantCode, agentUid, labels, navigate } = ctx;
  const teams = ctx.teams ?? [];
  // Act on the whole selection when the right-clicked row is part of a
  // multi-selection; otherwise act on just this row.
  const ids =
    ctx.selectedIds.includes(chat.id) && ctx.selectedIds.length > 1
      ? ctx.selectedIds
      : [chat.id];
  const multi = ids.length > 1;
  const tag = multi ? ` (${ids.length})` : '';
  const link = `${window.location.origin}/inbox/${chat.id}`;
  const hasContact = !!chat.contactId;

  const afterBulk = () => {
    if (multi) ctx.clearSelection();
  };
  const bulk = (action: 'status' | 'assign' | 'label_add' | 'label_remove' | 'snooze', value: unknown) =>
    api.bulkUpdateChats(tenantCode, agentUid, ids, { action, value }).then(afterBulk).catch(fail);
  const park = (reason: 'customer' | 'internal') =>
    api.bulkUpdateChats(tenantCode, agentUid, ids, { action: 'park', value: reason }).then(afterBulk).catch(fail);
  const each = <T,>(fn: (id: string) => Promise<T>) => Promise.all(ids.map(fn)).then(afterBulk).catch(fail);

  const statusItems: MenuNode[] = [
    chat.status === 'resolved'
      ? { id: 'st-open', label: 'Reopen', icon: <CircleDot className="w-3.5 h-3.5" />, onSelect: () => bulk('status', 'open') }
      : { id: 'st-resolve', label: 'Resolve', icon: <Check className="w-3.5 h-3.5" />, shortcut: 'R', onSelect: () => bulk('status', 'resolved') },
    { id: 'st-pending', label: 'Mark pending', icon: <Clock className="w-3.5 h-3.5" />, onSelect: () => bulk('status', 'pending') },
  ];

  const now = Date.now();
  const tomorrow9 = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  })();
  const snoozeItems: MenuNode[] = [
    { id: 'sn-1h', label: 'For 1 hour', onSelect: () => bulk('snooze', now + 60 * 60 * 1000) },
    { id: 'sn-3h', label: 'For 3 hours', onSelect: () => bulk('snooze', now + 3 * 60 * 60 * 1000) },
    { id: 'sn-tom', label: 'Until tomorrow 9am', onSelect: () => bulk('snooze', tomorrow9) },
    {
      id: 'sn-custom',
      label: 'Custom…',
      onSelect: () => {
        const h = prompt('Snooze for how many hours?');
        const n = h ? parseFloat(h) : NaN;
        if (Number.isFinite(n) && n > 0) bulk('snooze', now + n * 60 * 60 * 1000);
      },
    },
  ];

  // Park the conversation on someone else. Distinct from Snooze: snooze is "come
  // back to me in N hours", parking is "nothing to do here until they come back",
  // so it has no wake time and shows in its own tab until an agent un-parks it.
  const waitItems: MenuNode[] = [
    {
      id: 'wt-customer',
      label: 'Waiting for customer',
      checked: !multi && chat.waitReason === 'customer',
      onSelect: () => park('customer'),
    },
    {
      id: 'wt-internal',
      label: 'Waiting on internal team',
      checked: !multi && chat.waitReason === 'internal',
      onSelect: () => park('internal'),
    },
    { divider: true },
    {
      id: 'wt-none',
      label: 'Back to the queue',
      icon: <PlayCircle className="w-3.5 h-3.5" />,
      onSelect: () => each((id) => api.unpark(tenantCode, id)),
    },
  ];

  const assignItems: MenuNode[] = [
    { id: 'as-me', label: 'Assign to me', icon: <UserCheck className="w-3.5 h-3.5" />, shortcut: 'A', onSelect: () => bulk('assign', agentUid) },
    { id: 'as-auto', label: 'Auto-assign', icon: <Wand2 className="w-3.5 h-3.5" />, disabled: multi, onSelect: () => api.autoAssign(tenantCode, chat.id, agentUid, chat.inboxId).catch(fail) },
    { id: 'as-none', label: 'Unassign', icon: <UserX className="w-3.5 h-3.5" />, onSelect: () => bulk('assign', '') },
  ];

  const teamItems: MenuNode[] = [
    ...teams.map((tm) => ({
      id: `tm-${tm.id}`,
      label: tm.name,
      checked: !multi && chat.teamId === tm.id,
      onSelect: () => each((id) => api.assignToTeam(tenantCode, agentUid, id, tm.id)),
    })),
    ...(teams.length > 0 ? [{ divider: true as const }] : []),
    { id: 'tm-none', label: 'Remove from team', onSelect: () => each((id) => api.assignToTeam(tenantCode, agentUid, id, '')) },
  ];

  const priorityItems: MenuNode[] = PRIORITIES.map((p) => ({
    id: `pr-${p.value}`,
    label: p.label,
    checked: !multi && chat.priority === p.value,
    // (tenantCode, chatId, agentUid, priority) — the two middle args were the
    // wrong way round, so every priority change from this menu posted the
    // agent's uid as the chat id and silently changed nothing.
    onSelect: () => each((id) => api.setPriority(tenantCode, id, agentUid, p.value)),
  }));

  const current = new Set(chat.labels ?? []);
  const labelItems: MenuNode[] = [
    ...labels.map((l) => ({
      id: `lb-${l.id}`,
      label: l.name,
      checked: !multi && current.has(l.name),
      onSelect: () => bulk(current.has(l.name) ? 'label_remove' : 'label_add', l.name),
    })),
    ...(labels.length > 0 ? [{ divider: true as const }] : []),
    {
      id: 'lb-new',
      label: 'New label…',
      onSelect: () => {
        const name = prompt('Label to add:');
        if (name && name.trim()) bulk('label_add', name.trim());
      },
    },
  ];

  // Customer 360 + Manage lines read WP/subscription data that only exists for
  // a verified customer; for anonymous/identified chats they return empty
  // panels, so hide them. Block-contact is only effective for a verified
  // (stable) contactId — anonymous contactIds are session-scoped, so for those
  // we offer IP/device block instead. Verify identity is the de-anonymising
  // action, useful only before verification.
  const isVerified = chat.authLevel === 'verified';

  const noteItem: MenuNode = {
    id: 'cu-note',
    label: 'Add note…',
    icon: <StickyNote className="w-3.5 h-3.5" />,
    onSelect: () => {
      const note = prompt('Contact note:');
      if (note && note.trim()) inboxApi.addContactNote(tenantCode, agentUid, chat.contactId, note.trim()).catch(fail);
    },
  };
  const ticketItem: MenuNode = {
    id: 'cu-ticket',
    label: 'Create ticket…',
    icon: <Ticket className="w-3.5 h-3.5" />,
    onSelect: () => navigate(`/inbox/${chat.id}?cust=ticket`),
  };
  // Verified → block the stable contactId. Anonymous web visitor → block the
  // IP (only the web channel captures deviceInfo.ip). Non-web anonymous chats
  // have no effective block target, so omit the item entirely.
  const blockItem: MenuNode | null = isVerified
    ? { id: 'cu-block', label: 'Block contact', icon: <Ban className="w-3.5 h-3.5" />, danger: true, onSelect: () => navigate(`/inbox/${chat.id}?cust=block`) }
    : chat.channel === 'web'
      ? chat.ipBlocked
        ? {
            id: 'cu-ip-unblock',
            label: 'Unblock IP address',
            icon: <Ban className="w-3.5 h-3.5" />,
            onSelect: () => inboxApi.unblockIp(tenantCode, agentUid, chat.id).catch(fail),
          }
        : {
            id: 'cu-ip-block',
            label: 'Block IP address',
            icon: <Ban className="w-3.5 h-3.5" />,
            danger: true,
            onSelect: () => {
              if (!confirm('Block this IP address? Anonymous visitors from this address will be unable to start a chat.')) return;
              const reason = prompt('Reason for blocking (optional):') ?? undefined;
              inboxApi.blockIp(tenantCode, agentUid, chat.id, reason || undefined).catch(fail);
            },
          }
      : null;

  // Native Customer 360 + Manage lines are Olami-provisioning panels. For
  // WP-backed tenants (Bekesher) the WP side panel is the source of truth and
  // native line writes don't apply, so hide both — leaving Verify / Ticket /
  // Note / Block. Verify identity only shows pre-verification.
  const wpConfigured = ctx.wpConfigured === true;
  const customerItems: MenuNode[] = [
    ...(isVerified
      ? wpConfigured
        ? []
        : [
            { id: 'cu-panel', label: 'Open customer 360', icon: <User className="w-3.5 h-3.5" />, onSelect: () => navigate(`/inbox/${chat.id}?cust=panel`) },
            { id: 'cu-lines', label: 'Manage lines…', icon: <Smartphone className="w-3.5 h-3.5" />, onSelect: () => navigate(`/inbox/${chat.id}?cust=lines`) },
          ]
      : [{ id: 'cu-verify', label: 'Verify identity…', icon: <ShieldCheck className="w-3.5 h-3.5" />, onSelect: () => navigate(`/inbox/${chat.id}?cust=verify`) }]),
    ticketItem,
    noteItem,
    ...(blockItem ? [{ divider: true as const }, blockItem] : []),
  ];

  const menu: MenuNode[] = [
    { id: 'm-status', label: `Status${tag}`, icon: <CircleDot className="w-3.5 h-3.5" />, submenu: statusItems },
    { id: 'm-snooze', label: `Snooze${tag}`, icon: <Clock className="w-3.5 h-3.5" />, submenu: snoozeItems },
    ...(ctx.advanced
      ? [
          {
            id: 'm-wait',
            label: `Move to waiting${tag}`,
            icon: <PauseCircle className="w-3.5 h-3.5" />,
            submenu: waitItems,
          } as MenuNode,
        ]
      : []),
    { id: 'm-assign', label: `Assign${tag}`, icon: <UserCheck className="w-3.5 h-3.5" />, submenu: assignItems },
    { id: 'm-team', label: `Team${tag}`, icon: <Users className="w-3.5 h-3.5" />, submenu: teamItems },
    { id: 'm-priority', label: `Priority${tag}`, icon: <Flag className="w-3.5 h-3.5" />, submenu: priorityItems },
    { id: 'm-labels', label: `Labels${tag}`, icon: <Tag className="w-3.5 h-3.5" />, submenu: labelItems },
    { divider: true },
    chat.aiHandled
      ? { id: 'm-ai-take', label: 'Take over from AI', icon: <Bot className="w-3.5 h-3.5" />, disabled: multi, onSelect: () => api.toggleAi(tenantCode, agentUid, chat.id, true).catch(fail) }
      : { id: 'm-ai-give', label: 'Return to AI', icon: <Bot className="w-3.5 h-3.5" />, disabled: multi, onSelect: () => api.toggleAi(tenantCode, agentUid, chat.id, false).catch(fail) },
    chat.unreadCount > 0
      ? { id: 'm-read', label: 'Mark as read', icon: <MailOpen className="w-3.5 h-3.5" />, onSelect: () => each((id) => api.markRead(tenantCode, agentUid, id)) }
      : { id: 'm-unread', label: 'Mark as unread', icon: <Mail className="w-3.5 h-3.5" />, onSelect: () => each((id) => api.markUnread(tenantCode, agentUid, id)) },
    { id: 'm-customer', label: 'Customer', icon: <User className="w-3.5 h-3.5" />, disabled: !hasContact, submenu: customerItems },
    { divider: true },
    { id: 'm-newtab', label: 'Open in new tab', icon: <ExternalLink className="w-3.5 h-3.5" />, onSelect: () => window.open(link, '_blank', 'noopener') },
    { id: 'm-copy', label: 'Copy link', icon: <Link2 className="w-3.5 h-3.5" />, onSelect: () => navigator.clipboard?.writeText(link).catch(() => {}) },
  ];

  if (ctx.role === 'super_admin') {
    menu.push({ divider: true });
    menu.push({
      id: 'm-delete',
      label: multi ? `Delete ${ids.length} conversations` : 'Delete conversation',
      icon: <Trash2 className="w-3.5 h-3.5" />,
      danger: true,
      onSelect: () => {
        if (confirm(multi ? `Delete ${ids.length} conversations? This cannot be undone.` : 'Delete this conversation? This cannot be undone.')) {
          inboxApi.bulkDeleteChats(tenantCode, agentUid, ids).then(afterBulk).catch(fail);
        }
      },
    });
  }

  return menu;
}
